import { LuNavigation2 } from 'react-icons/lu'

import { MapMarker } from '@/feature/map'
import {
  MARKER_MOVING_COLOR,
  MARKER_STATIONARY_COLOR,
  TASK_MARKER,
} from '@/feature/observatory/utils/marker-constants'
import { useAssetTelemetry } from '@/feature/telemetry'

import { Tooltip } from '@/lib/core/components/Tooltip/Tooltip'
import { useSingleVehicle } from '@/lib/features/Assets/api'

import { MarkerBadge } from '@/components/MarkerBadge'

import { TaskDefinition } from '../../../types/task'
import styles from './TaskVehicleMarker.module.css'

// `pin` sets width only, keeping the marker artwork's 54:66 ratio. A little smaller than the
// observatory's 54, since this map is a modal pane rather than a full page.
const MARKER_SIZE = 44

/**
 * The assigned vehicle's live position on the task focus map.
 *
 * Purpose-built rather than reusing the Observatory's TaskAssignedAssetMarker, which resolves the
 * vehicle by scanning the whole fleet (useObservatoryAssets fetches every asset) and writes the
 * page store on click. Neither is acceptable in a modal that has to stay light and must not touch
 * the URL-persisted store. Here the vehicle is looked up by id and its position read straight from
 * the shared telemetry store, so the only cost is one already-deduped request plus a ref-count on
 * the app-wide telemetry socket.
 */
export const TaskVehicleMarker = ({ task }: { task: TaskDefinition }) => {
  const { data: vehicle } = useSingleVehicle(
    task.assignedVehicle ? { id: task.assignedVehicle.id } : null,
  )
  const telemetry = useAssetTelemetry(vehicle?.vehicleBridgeId ?? null)

  if (!telemetry) {
    return null
  }

  // The org's custom marker colours live behind useMarkerColors/useSettings, which this modal
  // deliberately doesn't pull in; these are the same defaults the Observatory falls back to.
  const color = telemetry.isMoving ? MARKER_MOVING_COLOR : MARKER_STATIONARY_COLOR
  const plateNumber = task.assignedVehicle?.plateNumber

  return (
    <MapMarker
      position={{ lat: telemetry.latitude, lng: telemetry.longitude }}
      zIndex={TASK_MARKER}
      clusterer={false}
    >
      <Tooltip
        placement="top"
        trigger="mouseenter"
        tooltipContent={<span className={styles.tooltip}>{plateNumber ?? 'Assigned vehicle'}</span>}
      >
        <div className={styles.marker} data-testid="task-focus-vehicle-marker">
          <MarkerBadge
            glyph={LuNavigation2}
            shape="pin"
            size={MARKER_SIZE}
            color={color}
            rotate={telemetry.heading ?? 0}
            filled
          />
        </div>
      </Tooltip>
    </MapMarker>
  )
}
